import React, { useMemo, useState } from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  TouchableWithoutFeedback,
  Pressable,
  ScrollView,
  Platform,
  ViewStyle,
  TextStyle,
  ActivityIndicator,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../theme";
import { ThemeColors } from "../theme/types";

interface LHModalPopProps {
  /**
   * Controls the visibility of the modal
   */
  visible: boolean;
  /**
   * Called when the modal should close
   */
  onClose: () => void;
  /**
   * Title of the modal
   */
  title: string;
  /**
   * Description/subtitle text below the title
   */
  description?: string;
  /**
   * Main content of the modal body
   */
  children?: React.ReactNode;
  /**
   * Shows a notification banner if true
   */
  showNotification?: boolean;
  /**
   * Notification message text
   */
  notificationMessage?: string;
  /**
   * Notification severity level
   */
  notificationSeverity?: "info" | "warning" | "error" | "success";
  /**
   * Shows the footer with buttons if true
   */
  showFooter?: boolean;
  /**
   * Primary button text (right button)
   */
  primaryButtonText?: string;
  /**
   * Secondary button text (left button)
   */
  secondaryButtonText?: string;
  /**
   * Tertiary button text (optional third button)
   */
  tertiaryButtonText?: string;
  /**
   * Called when primary button is pressed
   */
  onPrimaryPress?: () => void;
  /**
   * Called when secondary button is pressed
   */
  onSecondaryPress?: () => void;
  /**
   * Called when tertiary button is pressed
   */
  onTertiaryPress?: () => void;
  /**
   * Shows a close icon button in the top-right corner
   */
  showCloseButton?: boolean;
  /**
   * Enables scrolling for the body content
   */
  enableScroll?: boolean;
  /**
   * Custom styling for the modal container
   */
  containerStyle?: ViewStyle;
  /**
   * Custom styling for the title
   */
  titleStyle?: TextStyle;
  /**
   * Custom styling for the description
   */
  descriptionStyle?: TextStyle;
  /**
   * Loading state for primary button
   */
  primaryButtonLoading?: boolean;
  /**
   * Disabled state for primary button
   */
  primaryButtonDisabled?: boolean;
  /**
   * Disabled state for secondary button
   */
  secondaryButtonDisabled?: boolean;
  /**
   * Modal width (for web/desktop)
   */
  width?: number | string;
  /**
   * Animation type
   */
  animationType?: "none" | "slide" | "fade";
  /**
   * Backdrop blur/dismiss on outside click
   */
  dismissOnBackdropPress?: boolean;
}

const LHModalPop: React.FC<LHModalPopProps> = ({
  visible,
  onClose,
  title,
  description,
  children,
  showNotification = false,
  notificationMessage,
  notificationSeverity = "info",
  showFooter = true,
  primaryButtonText = "Delete",
  secondaryButtonText = "Cancel",
  tertiaryButtonText,
  onPrimaryPress,
  onSecondaryPress,
  onTertiaryPress,
  showCloseButton = true,
  enableScroll = true,
  containerStyle,
  titleStyle,
  descriptionStyle,
  primaryButtonLoading = false,
  primaryButtonDisabled = false,
  secondaryButtonDisabled = false,
  width = 538,
  animationType = "fade",
  dismissOnBackdropPress = true,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const handleBackdropPress = () => {
    if (dismissOnBackdropPress) {
      onClose();
    }
  };

  const handlePrimaryPress = () => {
    if (!primaryButtonLoading && !primaryButtonDisabled && onPrimaryPress) {
      onPrimaryPress();
    }
  };

  const handleSecondaryPress = () => {
    if (!secondaryButtonDisabled && onSecondaryPress) {
      onSecondaryPress();
    } else {
      onClose();
    }
  };

  const notificationColors = {
    info: colors.status.info,
    warning: colors.status.warning,
    error: colors.status.error,
    success: colors.status.success,
  };

  const bodyContent = enableScroll ? (
    <ScrollView
      style={styles.scrollableBody}
      showsVerticalScrollIndicator={true}
      scrollEnabled={true}
    >
      {children}
    </ScrollView>
  ) : (
    children
  );

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType={animationType}
      onRequestClose={onClose}
      statusBarTranslucent={true}
    >
      <View style={styles.modalOverlay}>
        <TouchableWithoutFeedback onPress={handleBackdropPress}>
          <View style={styles.modalBackground} />
        </TouchableWithoutFeedback>

        {/* Modal Container */}
        <View
          style={[
            styles.modalContainer,
            Platform.OS === "web" 
              ? { width } 
              : { width: '85%' as any },
            containerStyle,
          ]}
        >
          {/* Notification Banner */}
          {showNotification && (
            <View
              style={[
                styles.notificationBanner,
                {
                  backgroundColor: `${notificationColors[notificationSeverity]}15`,
                  borderLeftColor: notificationColors[notificationSeverity],
                },
              ]}
            >
              <MaterialIcons
                name={
                  notificationSeverity === "error"
                    ? "error"
                    : notificationSeverity === "warning"
                      ? "warning"
                      : notificationSeverity === "success"
                        ? "check-circle"
                        : "info"
                }
                size={20}
                color={notificationColors[notificationSeverity]}
                style={styles.notificationIcon}
              />
              <Text
                style={[
                  styles.notificationText,
                  { color: notificationColors[notificationSeverity] },
                ]}
              >
                {notificationMessage}
              </Text>
            </View>
          )}

          {/* Close Button */}
          {showCloseButton && (
            <Pressable style={styles.closeButton} onPress={onClose}>
              <MaterialIcons name="close" size={24} color={colors.text.primary} />
            </Pressable>
          )}

          {/* Header */}
          <View style={styles.header}>
            <Text style={[styles.title, titleStyle]}>{title}</Text>
          </View>

          {/* Description */}
          {description && (
            <Text style={[styles.description, descriptionStyle]}>
              {description}
            </Text>
          )}

          {/* Body Content */}
          <View style={styles.bodyContainer}>{bodyContent}</View>

          {/* Footer with Buttons */}
          {showFooter && (
            <View style={styles.footer}>
              <View style={styles.buttonContainer}>
                {/* Tertiary Button (if provided) */}
                {tertiaryButtonText && (
                  <TouchableOpacity
                    style={[
                      styles.button,
                      styles.tertiaryButton,
                      secondaryButtonDisabled && styles.buttonDisabled,
                    ]}
                    onPress={onTertiaryPress || onClose}
                    disabled={secondaryButtonDisabled}
                  >
                    <Text style={styles.tertiaryButtonText}>
                      {tertiaryButtonText}
                    </Text>
                  </TouchableOpacity>
                )}

                {/* Secondary Button */}
                {secondaryButtonText && (
                  <TouchableOpacity
                    style={[
                      styles.button,
                      styles.secondaryButton,
                      secondaryButtonDisabled && styles.buttonDisabled,
                    ]}
                    onPress={handleSecondaryPress}
                    disabled={secondaryButtonDisabled}
                  >
                    <Text style={styles.secondaryButtonText}>
                      {secondaryButtonText}
                    </Text>
                  </TouchableOpacity>
                )}

                {/* Primary Button */}
                <TouchableOpacity
                  style={[
                    styles.button,
                    styles.primaryButton,
                    (primaryButtonDisabled || primaryButtonLoading) &&
                      styles.buttonDisabled,
                  ]}
                  onPress={handlePrimaryPress}
                  disabled={primaryButtonDisabled || primaryButtonLoading}
                >
                  {primaryButtonLoading ? (
                    <ActivityIndicator size="small" color={colors.text.inverse} />
                  ) : (
                    <Text style={styles.primaryButtonText}>
                      {primaryButtonText}
                    </Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

const createStyles = (colors: ThemeColors, isDark = false) =>
  StyleSheet.create({
    // Overlay
    modalOverlay: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      height: "100%",
    },
    modalBackground: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: colors.overlay,
    },

    // Modal Container
    modalContainer: {
      backgroundColor: colors.background.surface,
      borderRadius: 8,
      borderColor: colors.border.default,
      borderWidth: 10,
      shadowColor: colors.brand.primary,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.12,
      shadowRadius: 24,
      elevation: 12,
      maxHeight: "90%",
      overflow: "hidden",
    },

    // Close Button
    closeButton: {
      position: "absolute",
      top: 16,
      right: 16,
      zIndex: 10,
      padding: 8,
      borderRadius: 4,
    },

    // Notification Banner
    notificationBanner: {
      flexDirection: "row",
      alignItems: "center",
      padding: 12,
      borderLeftWidth: 4,
      gap: 12,
    },
    notificationIcon: {
      marginRight: 4,
    },
    notificationText: {
      fontSize: 14,
      fontWeight: "500",
      flex: 1,
    },

    // Header
    header: {
      paddingHorizontal: 32,
      paddingTop: 32,
      paddingBottom: 12,
    },
    title: {
      fontSize: 24,
      fontWeight: "700",
      lineHeight: 32,
      color: colors.text.primary,
    },

    // Description
    description: {
      paddingHorizontal: 32,
      paddingVertical: 8,
      fontSize: 14,
      fontWeight: "400",
      lineHeight: 20,
      color: colors.text.primary,
      marginBottom: 8,
    },

    // Body Container
    bodyContainer: {
      flex: 1,
      paddingHorizontal: 32,
      // paddingVertical: 16,
      paddingBottom: 16,
      // minHeight: 60,
    },
    scrollableBody: {
      flex: 1,
    },

    // Footer
    footer: {
      paddingHorizontal: 32,
      // paddingVertical: 24,
      paddingBottom: 24,
      // borderTopWidth: 1,
      borderTopColor: colors.border.divider,
    },
    buttonContainer: {
      flexDirection: "row",
      gap: 16,
      justifyContent: "flex-end",
      alignItems: "center",
    },

    // Buttons
    button: {
      paddingVertical: 8,
      paddingHorizontal: 20,
      borderRadius: 24,
      minHeight: 32,
      justifyContent: "center",
      alignItems: "center",
    },
    primaryButton: {
      backgroundColor: colors.brand.primary,
      minWidth: 80,
    },
    primaryButtonText: {
      color: colors.text.inverse,
      fontSize: 14,
      fontWeight: "700",
      lineHeight: 20,
    },
    secondaryButton: {
      backgroundColor: colors.background.surface,
      minWidth: 80,
      ...(isDark ? { borderWidth: 1, borderColor: colors.border.default } : {}),
    },
    secondaryButtonText: {
      color: colors.brand.primary,
      fontSize: 14,
      fontWeight: "700",
      lineHeight: 20,
    },
    tertiaryButton: {
      backgroundColor: colors.background.page,
      borderWidth: 1,
      borderColor: colors.border.default,
    },
    tertiaryButtonText: {
      color: colors.text.primary,
      fontSize: 14,
      fontWeight: "700",
      lineHeight: 20,
    },
    buttonDisabled: {
      opacity: 0.5,
    },
  });

export default LHModalPop;
